import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Pause, Play, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  createOrganization,
  listOrganizations,
  setOrgStatus,
  type OrgRow,
} from '@/lib/super-admin-api';

const PAGE_SIZE = 20;

function suggestSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function SuperAdminOrganizationsPage() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(searchParams.get('action') === 'new');
  const [confirmStatus, setConfirmStatus] = useState<{
    org: OrgRow;
    target: 'ACTIVE' | 'SUSPENDED';
  } | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!createOpen && searchParams.get('action') === 'new') {
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [createOpen, searchParams, setSearchParams]);

  const query = useQuery({
    queryKey: ['super-admin', 'organizations', { debouncedSearch, status, page }],
    queryFn: () =>
      listOrganizations({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(status !== 'ALL' ? { status } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusMutation = useMutation({
    mutationFn: ({ id, target }: { id: string; target: 'ACTIVE' | 'SUSPENDED' }) =>
      setOrgStatus(id, target),
    onSuccess: (_res, vars) => {
      success(vars.target === 'SUSPENDED' ? 'Organization suspended' : 'Organization reactivated');
      qc.invalidateQueries({ queryKey: ['super-admin', 'organizations'] });
      qc.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] });
      setConfirmStatus(null);
    },
    onError: (e) => error('Status change failed', extractApiErrorMessage(e)),
  });

  const empty = !query.isLoading && (query.data?.items.length ?? 0) === 0;

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <PageHeader
        title="Organizations"
        description="Create, suspend, and monitor tenant organizations."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> New organization
          </Button>
        }
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="flex-1 min-w-[240px]">
            <Label
              htmlFor="org-search"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Search
            </Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id="org-search"
                className="pl-9"
                placeholder="Search by name, slug, or admin email"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
          <div className="w-40">
            <Label
              htmlFor="org-status"
              className="mb-1 block text-xs uppercase text-muted-foreground"
            >
              Status
            </Label>
            <Select
              id="org-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as typeof status);
                setPage(0);
              }}
            >
              <option value="ALL">All</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {query.isError ? (
        <EmptyState
          icon={Building2}
          title="Could not load organizations"
          description={extractApiErrorMessage(query.error)}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : empty ? (
        <EmptyState
          icon={Building2}
          title={
            debouncedSearch || status !== 'ALL'
              ? 'No matching organizations'
              : 'No organizations yet'
          }
          description={
            debouncedSearch || status !== 'ALL'
              ? 'Clear your filters to see every tenant.'
              : 'Provision the first tenant by creating a new organization.'
          }
          action={
            !debouncedSearch && status === 'ALL' ? (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1 h-4 w-4" aria-hidden /> New organization
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Admin email</TableHead>
                  <TableHead className="text-right">Members</TableHead>
                  <TableHead className="text-right">Projects</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.isLoading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <TableCell key={`sk-${i}-${j}`}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : query.data?.items.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {org.slug}
                        </TableCell>
                        <TableCell>
                          {org.adminEmail || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{org.memberCount}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {org.projectCount}
                        </TableCell>
                        <TableCell>
                          {org.status === 'ACTIVE' ? (
                            <Badge variant="success">Active</Badge>
                          ) : (
                            <Badge variant="warning">Suspended</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {org.status === 'ACTIVE' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmStatus({ org, target: 'SUSPENDED' })}
                            >
                              <Pause className="mr-1 h-3 w-3" aria-hidden /> Suspend
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => setConfirmStatus({ org, target: 'ACTIVE' })}
                            >
                              <Play className="mr-1 h-3 w-3" aria-hidden /> Reactivate
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {page + 1} of {totalPages} · {total} total
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <CreateOrganizationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['super-admin', 'organizations'] });
          qc.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] });
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmStatus)}
        title={
          confirmStatus?.target === 'SUSPENDED'
            ? `Suspend ${confirmStatus.org.name}?`
            : `Reactivate ${confirmStatus?.org.name ?? ''}?`
        }
        description={
          confirmStatus?.target === 'SUSPENDED'
            ? 'Members of this organization will be unable to sign in until you reactivate it.'
            : 'Members will regain access and can sign in again.'
        }
        confirmLabel={confirmStatus?.target === 'SUSPENDED' ? 'Suspend' : 'Reactivate'}
        destructive={confirmStatus?.target === 'SUSPENDED'}
        onCancel={() => setConfirmStatus(null)}
        onConfirm={() => {
          if (!confirmStatus) return Promise.resolve();
          return statusMutation.mutateAsync({
            id: confirmStatus.org.id,
            target: confirmStatus.target,
          });
        }}
      />
    </div>
  );
}

function CreateOrganizationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: () => void;
}) {
  const { success, error } = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugDirty, setSlugDirty] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const computedSlug = useMemo(
    () => (slugDirty ? slug : suggestSlug(name)),
    [name, slug, slugDirty],
  );

  useEffect(() => {
    if (!open) {
      setName('');
      setSlug('');
      setSlugDirty(false);
      setAdminName('');
      setAdminEmail('');
      setSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await createOrganization({
        name: name.trim(),
        slug: computedSlug,
        adminEmail: adminEmail.trim().toLowerCase(),
        adminName: adminName.trim(),
      });
      success('Organization created', 'Admin invitation email queued');
      onCreated();
      onOpenChange(false);
    } catch (err) {
      error('Could not create organization', extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            Provision a new tenant and invite its first admin. Default roles are installed
            automatically.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div className="space-y-2">
            <Label htmlFor="org-name">Organization name</Label>
            <Input
              id="org-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corp"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="org-slug">URL slug</Label>
            <Input
              id="org-slug"
              required
              pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
              value={computedSlug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugDirty(true);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Used in the URL: /o/{computedSlug || 'your-slug'}/…
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Admin name</Label>
              <Input
                id="admin-name"
                required
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Admin email</Label>
              <Input
                id="admin-email"
                type="email"
                required
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create and invite admin'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
